import React, { useState, useEffect, useContext } from 'react';
import { EventContext } from '../context/EventContext';
import { getScorecardsByEventId, createScorecard, setScorecardAsDefault, updateScorecard, removeScorecard } from '../services/scorecardService';
import { Scorecard, Question } from '../types/Scorecard';
import { useToast } from '../context/ToastContext';

interface ScorecardDashboardProps {
    itemsPerPage?: number;
}

const ScorecardDashboard: React.FC<ScorecardDashboardProps> = ({ itemsPerPage = 10 }) => {
    const { currentEvent } = useContext(EventContext);
    const [scorecards, setScorecards] = useState<Scorecard[]>([]);
    const [filteredScorecards, setFilteredScorecards] = useState<Scorecard[]>([]);
    const [currentScorecard, setCurrentScorecard] = useState<Scorecard | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [editScorecard, setEditScorecard] = useState<Scorecard | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [isAddingQuestion, setIsAddingQuestion] = useState(false);
    const { addToast } = useToast();

    // Fetch scorecards for the current event
    useEffect(() => {
        const fetchScorecards = async () => {
            if (currentEvent) {
                const eventScorecards = await getScorecardsByEventId(currentEvent.event_id);
                setScorecards(eventScorecards);
                setFilteredScorecards(eventScorecards);
            }
        };

        fetchScorecards();
    }, [currentEvent]);

    // Handle search functionality
    useEffect(() => {
        if (searchQuery) {
            setFilteredScorecards(
                scorecards.filter((scorecard) =>
                    scorecard.scorecard_name.toLowerCase().includes(searchQuery.toLowerCase())
                )
            );
        } else {
            setFilteredScorecards(scorecards);
        }
    }, [searchQuery, scorecards]);

    // Handle scorecard editing
    const handleEdit = (scorecard: Scorecard) => {
        setEditScorecard(scorecard);
        setIsEditing(true);
    };

    // Handle scorecard creation
    const handleCreate = () => {
        if (currentEvent && !currentEvent.is_locked) {
            setEditScorecard({
                scorecard_id: '',
                scorecard_name: '',
                event_id: currentEvent?.event_id,
                host_id: currentEvent.host_id,
                question_data: [],
                is_default: false,
            });
            setIsCreating(true);
            setIsEditing(true);
        }
    };

    const handleRemoveQuestion = (indexToRemove: number) => {
        if (editScorecard && editScorecard.question_data) {
            const updatedQuestions = editScorecard.question_data.filter(
                (_, index) => index !== indexToRemove
            );
            setEditScorecard({
                ...editScorecard,
                question_data: updatedQuestions,
            });
        }
    };


    // Save scorecard changes (create or update)
    const handleSaveEdit = async () => {
        if (editScorecard && editScorecard.scorecard_name) {
            if (isCreating) {
                try {
                    const newScorecard = await createScorecard(editScorecard);
                    if (newScorecard) {
                        setScorecards([...scorecards, newScorecard]);
                        setFilteredScorecards([...scorecards, newScorecard]);
                    } else {
                        if (currentEvent) {
                            const eventScorecards = await getScorecardsByEventId(currentEvent.event_id);
                            setScorecards(eventScorecards);
                            setFilteredScorecards(eventScorecards);
                        }
                    }
                } catch (error) {
                    addToast('Error creating scorecard', true);
                    console.error('Error creating scorecard:', error);
                }
                setIsCreating(false);
            } else {
                try {
                    // Update the scorecard with the new question data
                    const updatedScorecard = await updateScorecard(editScorecard.scorecard_id, editScorecard);
                    const updatedScorecards = scorecards.map((scorecard) =>
                        scorecard.scorecard_id === editScorecard?.scorecard_id ? editScorecard : scorecard
                    );
                    setScorecards(updatedScorecards);
                    setFilteredScorecards(updatedScorecards);
                } catch (error) {
                    addToast('Error updating scorecard', true);
                    console.error('Error updating scorecard:', error);
                }
            }
            setIsEditing(false);
        }
    };

    // Handle scorecard deletion
    const handleDelete = async () => {
        if (editScorecard) {
            try {
                await removeScorecard(editScorecard.scorecard_id);
                const updatedScorecards = scorecards.filter(
                    (scorecard) => scorecard.scorecard_id !== editScorecard.scorecard_id
                );
                setScorecards(updatedScorecards);
                setFilteredScorecards(updatedScorecards);
                setIsEditing(false);
                setIsCreating(false);
            } catch (error) {
                console.error('Error deleting scorecard:', error);
            }
        }
    };

    // Handle adding questions
    const handleAddQuestion = () => {
        if (editScorecard) {
            if (editScorecard.question_data && editScorecard.question_data.length < 29) {
                const newQuestion: Question = {
                    question: '',
                    point_value: editScorecard.question_data?.length
                        ? editScorecard.question_data[editScorecard.question_data.length - 1].point_value
                        : 10, // Start with 10 points for the new question
                };
                setEditScorecard({
                    ...editScorecard,
                    question_data: [...(editScorecard.question_data || []), newQuestion],
                });
            } else {
                addToast('Scorecard limit of 30 questions reached.')
            }
        }
    };

    // Handle question point value change
    const handleQuestionPointValueChange = (questionIndex: number, value: number) => {
        if (editScorecard && editScorecard.question_data) {
            const updatedQuestions = editScorecard.question_data.map((question, index) =>
                index === questionIndex ? { ...question, point_value: value } : question
            );
            setEditScorecard({
                ...editScorecard,
                question_data: updatedQuestions,
            });
        }
    };


    const handleQuestionTextChange = (questionIndex: number, value: string) => {
        if (editScorecard && editScorecard.question_data) {
            const updatedQuestions = editScorecard.question_data.map((question, index) =>
                index === questionIndex ? { ...question, question: value } : question
            );
            setEditScorecard({
                ...editScorecard,
                question_data: updatedQuestions,
            });
        }
    };

    // Set scorecard as default
    const handleSetAsDefault = async () => {
        const updatedScorecards = scorecards.map((scorecard) => {
            // Set `is_default` to true only for the scorecard with the id of `editScorecard`
            if (scorecard.scorecard_id === editScorecard?.scorecard_id) {
                return { ...scorecard, is_default: true };
            }
            return { ...scorecard, is_default: false };
        });

        if (editScorecard) {
            try {
                await setScorecardAsDefault(editScorecard.scorecard_id);
                editScorecard.is_default = true;
                setScorecards(updatedScorecards);
                // setIsEditing(false);
            } catch (error) {
                console.error('Failed to set scorecard as default:', error);
            }
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <h1>Scorecards for {currentEvent?.event_name}</h1>

            {/* Search Bar */}
            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Search scorecards..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            <div className="card"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap', // Allow wrapping of group items to the next line
                    gap: '10px',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    backgroundColor: '#2c3e50'
                }}
            >

                {/* Scorecard List */}
                {filteredScorecards.map((scorecard) => (
                    <div key={scorecard.scorecard_id} className='card'

                        style={{
                            flex: '0 0 auto',
                            padding: '20px',
                            border: scorecard.is_default == true ? '2px solid green' : '1px solid #ccc',
                            borderRadius: '5px',
                            width: '200px', // Ensures each group takes up at least 200px
                            textAlign: 'center',
                            cursor: 'pointer',
                            position: 'relative', // For the marker
                            backgroundColor: 'whitesmoke'
                        }}



                    >
                        <h3>{scorecard.scorecard_name}</h3>
                        <button onClick={() => handleEdit(scorecard)} disabled={currentEvent?.is_locked}>Edit</button>
                    </div>
                ))}

                {/* Create Scorecard Button */}
                <div
                    className="card"
                    style={{
                        flex: '0 0 auto',
                        padding: '20px',
                        border: '1px dashed #ccc',
                        borderRadius: '5px',
                        minWidth: '200px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: '#f8f9fa',
                    }}
                    onClick={handleCreate}
                >
                    <h3>Create Scorecard</h3>
                    <h1>+</h1>
                </div>
            </div>

            {/* Edit Scorecard Popup */}
            {isEditing && editScorecard && (
                <div
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'white',
                        padding: '20px',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        zIndex: 1000,
                        width: '90%',
                        maxWidth: 800
                    }}
                >   <div style={{ display: 'flex', gap: '10px' }}>
                        <h3>{isCreating ? 'Create Scorecard' : 'Edit Scorecard'}  {editScorecard.is_default ? '(Default)' : ''}</h3>
                        {!isCreating && !editScorecard.is_default && (<button onClick={handleSetAsDefault} style={{ maxHeight: '40px', marginLeft: 'auto' }}
                        >Set Default</button>)}
                    </div>
                    {/* Scorecard Name */}
                    <div>
                        <label>Scorecard Name:</label>
                        <input
                            type="text"
                            value={editScorecard.scorecard_name}
                            onChange={(e) =>
                                setEditScorecard({ ...editScorecard, scorecard_name: e.target.value })
                            }
                        />
                    </div>

                    {/* Questions */}
                    <div>
                        <h4>Questions</h4>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                            {editScorecard.question_data &&
                                editScorecard.question_data.map((question, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <label>Question {index + 1}:</label>
                                        <input
                                            style={{ maxWidth: '350px' }}
                                            type="text"
                                            value={question.question}
                                            onChange={(e) => handleQuestionTextChange(index, e.target.value)}
                                        />
                                        <label>Max Point Value:</label>
                                        <input style={{ maxWidth: '60px' }}
                                            type="number"
                                            value={question.point_value}
                                            onChange={(e) => handleQuestionPointValueChange(index, parseInt(e.target.value))}
                                            min={0}
                                            max={100}
                                        />
                                        <button
                                            onClick={() => handleRemoveQuestion(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            <button onClick={handleAddQuestion}>Add Question</button>
                        </div>
                    </div>
                    <br/>


                    {/* Save and Delete Buttons */}
                    <div className="buttonsRow">
                        <div className="buttonsLeft">
                            <button onClick={handleDelete} className='warningButton'>Delete Scorecard</button>

                        </div>
                        <div className="buttonsRight">
                            <button onClick={handleSaveEdit} disabled={editScorecard.scorecard_name == '' || !editScorecard.scorecard_name}>Save</button>
                            <button onClick={() => setIsEditing(false)} className='cancelButton'>Cancel</button>

                        </div>
                    </div>
                </div>
            )}

            {/* Backdrop for Popup */}
            {isEditing && (
                <div className='popOverBackdrop'
                    onClick={() => setIsEditing(false)}
                />
            )}
        </div>
    );
};

export default ScorecardDashboard;
